import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, LayoutGroup, LazyMotion, MotionConfig, domMax, m } from 'framer-motion';
import './show-tracker.css';
import { Empty, SaveButton, UndoToast, useUndo, WinBar } from './components/Feedback.js';
import { History, Leaderboard, Roster } from './components/Lists.js';
import { OddsPanel } from './components/Odds.js';
import { Hint, Ovr, Roll, SwapIcon, Tabs } from './components/primitives.js';
import { Score } from './components/Score.js';
import { spring, timing } from './motion.js';
import {
  RATING,
  computeLeague,
  expectedScore,
  ovrOf,
  previewDeltas,
  standardInnings,
} from '../../utils/mlb-the-show/rating.js';
import { signed, todayISO } from '../../utils/mlb-the-show/format.js';

const latestGame = (games) => games.reduce((latest, game) => (!latest || game.createdAt > latest.createdAt ? game : latest), null);

export default function ShowTracker({ players, games, onAddPlayer, onRemovePlayer, onAddGame, onRemoveGame }) {
  const league = useMemo(() => computeLeague(players, games), [players, games]);
  const stdInnings = useMemo(() => standardInnings(games), [games]);

  const [home, setHome] = useState(() => latestGame(games)?.home ?? players[0]?.id ?? '');
  const [away, setAway] = useState(() => latestGame(games)?.away ?? players[1]?.id ?? '');
  const [homeScore, setHomeScore] = useState(0);
  const [awayScore, setAwayScore] = useState(0);
  const [innings, setInnings] = useState(stdInnings);
  const [date, setDate] = useState(todayISO);
  const [tab, setTab] = useState('board');
  const [lastGame, setLastGame] = useState(null);
  const [saved, setSaved] = useState(false);
  const [tieWarning, setTieWarning] = useState(false);
  const [swaps, setSwaps] = useState(0);
  const savedTimer = useRef(null);
  const undo = useUndo();

  useEffect(() => () => clearTimeout(savedTimer.current), []);

  // Keep the matchup valid as players are added or removed.
  useEffect(() => {
    const ids = players.map((p) => p.id);
    let nextHome = ids.includes(home) ? home : '';
    let nextAway = ids.includes(away) && away !== nextHome ? away : '';
    if (!nextHome) nextHome = ids.find((id) => id !== nextAway) ?? '';
    if (!nextAway) nextAway = ids.find((id) => id !== nextHome) ?? '';
    if (nextHome !== home) setHome(nextHome);
    if (nextAway !== away) setAway(nextAway);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [players]);

  useEffect(() => {
    if (homeScore !== awayScore) setTieWarning(false);
  }, [homeScore, awayScore]);

  const nameOf = (id) => players.find((p) => p.id === id)?.name ?? '—';
  const homeChance = expectedScore(league.elo[home] ?? RATING.baseElo, league.elo[away] ?? RATING.baseElo);

  const swap = () => {
    setHome(away);
    setAway(home);
    setHomeScore(awayScore);
    setAwayScore(homeScore);
    setSwaps((n) => n + 1);
  };

  const pick = (side, id) => {
    if (side === 'home') {
      if (id === away) swap();
      else setHome(id);
    } else if (id === home) swap();
    else setAway(id);
  };

  const save = () => {
    if (players.length < 2 || !home || !away) return false;
    if (homeScore === awayScore) {
      setTieWarning(true);
      return false;
    }
    const game = onAddGame({ home, away, homeScore, awayScore, innings, date });
    setLastGame(game);
    setSaved(true);
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaved(false), timing.savedLabelMs);
    setHomeScore(0);
    setAwayScore(0);
    setInnings(stdInnings);
    return true;
  };

  const deleteGame = (game) => {
    const restore = onRemoveGame(game.id);
    undo.show(`Deleted ${nameOf(game.away)} ${game.awayScore}–${game.homeScore} ${nameOf(game.home)}`, restore);
  };

  const removePlayer = (player) => {
    const count = games.filter((g) => g.home === player.id || g.away === player.id).length;
    const restore = onRemovePlayer(player.id);
    undo.show(`Removed ${player.name}${count ? ` and ${count} game${count > 1 ? 's' : ''}` : ''}`, restore);
  };

  let hintKey;
  let hint;
  if (tieWarning) {
    hintKey = 'tie';
    hint = <span className="st-warn">Games can't end tied. Add a run or play extra innings.</span>;
  } else if (homeScore === awayScore) {
    hintKey = 'idle';
    hint = homeScore === 0 ? 'Tap a score to set it, or click to add a run' : 'Tied. Add a run or extra innings.';
  } else {
    const d = previewDeltas(league, home, away, homeScore, awayScore);
    hintKey = 'preview';
    hint = (
      <span>
        If saved: {nameOf(away)} <span className={d.away >= 0 ? 'st-up' : 'st-down'}>{signed(d.away)}</span> ·{' '}
        {nameOf(home)} <span className={d.home >= 0 ? 'st-up' : 'st-down'}>{signed(d.home)}</span>
      </span>
    );
  }

  const side = (which) => {
    const id = which === 'home' ? home : away;
    const score = which === 'home' ? homeScore : awayScore;
    const setScore = which === 'home' ? setHomeScore : setAwayScore;
    const other = which === 'home' ? awayScore : homeScore;
    const lg = lastGame ? league.perGame[lastGame.id] : undefined;
    let delta = 0;
    if (lg && lastGame.home === id) delta = lg.homeDelta;
    else if (lg && lastGame.away === id) delta = lg.awayDelta;
    const sideLabel = which === 'home' ? 'Home' : 'Away';
    return (
      <div className="st-slot">
        <span className={`st-slot-tag st-slot-tag--${which}`} aria-hidden="true">
          {sideLabel.toUpperCase()}
        </span>
        <m.div key={id} layoutId={`side-${id}`} className="st-side" transition={spring.standard}>
          <select
            className="st-select"
            aria-label={`${sideLabel} player`}
            value={id}
            onChange={(e) => pick(which, e.target.value)}
          >
            {players.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <Ovr value={ovrOf(league, id)} delta={delta} deltaKey={delta ? lastGame.id : null} />
          <Score value={score} onChange={setScore} label={`${sideLabel}, ${nameOf(id)}`} leading={score > other} />
        </m.div>
      </div>
    );
  };

  return (
    <LazyMotion features={domMax} strict>
      <MotionConfig reducedMotion="user" transition={spring.standard}>
        <section className="show-tracker" aria-label="MLB The Show tracker">
          <div className="st-card">
            {players.length < 2 ? (
              <Empty>Add at least two players on the Players tab to start logging games.</Empty>
            ) : (
              <>
                <LayoutGroup id="st-match">
                  <div className="st-match">
                    {side('away')}
                    <div className="st-mid">
                      <m.button
                        type="button"
                        className="st-swap"
                        aria-label="Swap players between home and away"
                        onClick={swap}
                        animate={{ rotate: swaps * 180 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <SwapIcon />
                      </m.button>
                      <span className="st-vs">VS</span>
                    </div>
                    {side('home')}
                  </div>
                </LayoutGroup>
                <WinBar homeName={nameOf(home)} awayName={nameOf(away)} homeChance={homeChance} />
                <div className="st-meta">
                  <div className="st-field">
                    <span className="st-fl">
                      Innings{innings > stdInnings && <em>Extras</em>}
                    </span>
                    <div className="st-inn">
                      <button
                        type="button"
                        className="st-step"
                        aria-label="Fewer innings"
                        disabled={innings <= 1}
                        onClick={() => setInnings((i) => i - 1)}
                      >
                        −
                      </button>
                      <Roll value={innings} />
                      <button
                        type="button"
                        className="st-step"
                        aria-label="More innings"
                        disabled={innings >= 20}
                        onClick={() => setInnings((i) => i + 1)}
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <label className="st-field">
                    <span className="st-fl">Date</span>
                    <input type="date" value={date} onChange={(e) => setDate(e.target.value || todayISO())} />
                  </label>
                </div>
                <SaveButton onSave={save} saved={saved} />
                <Hint messageKey={hintKey}>{hint}</Hint>
              </>
            )}
          </div>

          <Tabs
            id="st-tabs"
            label="Tracker views"
            value={tab}
            onChange={setTab}
            options={[
              { value: 'board', label: 'Leaderboard' },
              { value: 'history', label: 'History', count: games.length },
              { value: 'players', label: 'Players', count: players.length },
              { value: 'odds', label: 'Odds' },
            ]}
          />

          <AnimatePresence mode="wait" initial={false}>
            <m.div
              key={tab}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.14 }}
            >
              {tab === 'board' && <Leaderboard players={players} league={league} lastGame={lastGame} />}
              {tab === 'history' && (
                <History
                  players={players}
                  games={games}
                  league={league}
                  standardInnings={stdInnings}
                  highlightId={lastGame?.id}
                  onDelete={deleteGame}
                />
              )}
              {tab === 'players' && (
                <Roster players={players} league={league} onAdd={onAddPlayer} onRemove={removePlayer} />
              )}
              {tab === 'odds' &&
                (players.length < 2 ? (
                  <Empty>Add two players to see odds.</Empty>
                ) : (
                  <OddsPanel homeName={nameOf(home)} awayName={nameOf(away)} homeChance={homeChance} games={games} />
                ))}
            </m.div>
          </AnimatePresence>

          <UndoToast state={undo} />
        </section>
      </MotionConfig>
    </LazyMotion>
  );
}
